import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import type { AccordionItem, AccordionProps } from './types'
import './Accordion.css'

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

/** Filtra las filas cuyo título o contenido contiene el término (sin acentos ni mayúsculas). */
export function filterAccordionItems(items: AccordionItem[], query: string | undefined): AccordionItem[] {
  const q = normalize((query ?? '').trim())
  if (!q) return items
  return items.filter((it) => {
    const text = typeof it.content === 'string' ? it.content : (it.searchText ?? '')
    return normalize(it.title).includes(q) || normalize(text).includes(q)
  })
}

/** Resalta las apariciones del término dentro de un texto. */
export function Highlight({ text, query }: { text: string; query?: string | undefined }): ReactNode {
  const q = (query ?? '').trim()
  if (!q) return text
  const parts: ReactNode[] = []
  const lower = normalize(text)
  const needle = normalize(q)
  let i = 0
  let k = 0
  while (i < text.length) {
    const j = lower.indexOf(needle, i)
    if (j < 0) {
      parts.push(text.slice(i))
      break
    }
    if (j > i) parts.push(text.slice(i, j))
    parts.push(
      <mark key={k++} className="hp-accordion__mark">
        {text.slice(j, j + q.length)}
      </mark>,
    )
    i = j + q.length
  }
  return parts
}

/** Acordeón (DESIGN.md §4): filas de 56 px con título h3 y chevron; contenido con padding 16; buscador que resalta. */
export function Accordion({ items, defaultOpenId = null, openId, onToggle, query, headingLevel = 3, className }: AccordionProps) {
  const uid = useId()
  const Heading = headingLevel === 2 ? 'h2' : 'h3'
  const [internal, setInternal] = useState<string | null>(defaultOpenId)
  const visible = filterAccordionItems(items, query)
  const hasQuery = Boolean(query?.trim())
  const controlled = openId !== undefined
  const current = controlled ? openId : internal
  // Al buscar se abre el primer resultado si la fila abierta no está entre los resultados (PantallaGuia.dc.html).
  const effectiveOpen = hasQuery && (current === null || !visible.some((it) => it.id === current)) ? (visible[0]?.id ?? null) : current

  const toggle = (id: string) => {
    const next = effectiveOpen === id ? null : id
    if (!controlled) setInternal(next)
    onToggle?.(next)
  }

  return (
    <div className={cx('hp-accordion', className)}>
      {visible.map((it) => {
        const open = effectiveOpen === it.id
        const panelId = `hp-acc-${uid}-${it.id}`
        return (
          <div key={it.id} className="hp-accordion__item">
            <Heading className="hp-accordion__heading">
              <button type="button" aria-expanded={open} aria-controls={panelId} onClick={() => toggle(it.id)} className="hp-accordion__trigger">
                <span className="hp-accordion__title">
                  {it.icon && <Icon name={it.icon} size={22} className="hp-accordion__icon" />}
                  <span>
                    <Highlight text={it.title} query={query} />
                  </span>
                </span>
                <Icon name="chevron-down" size={22} className={cx('hp-accordion__chevron', open && 'hp-accordion__chevron--open')} />
              </button>
            </Heading>
            <div id={panelId} hidden={!open} className="hp-accordion__panel">
              {typeof it.content === 'string' ? <Highlight text={it.content} query={query} /> : it.content}
            </div>
          </div>
        )
      })}
    </div>
  )
}
